import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useNotify, useRedirect } from 'react-admin';
import {
  Box, Card, CardContent, Typography, Stack, Button, TextField,
  Autocomplete, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Divider, Alert, Tooltip,
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon   from '@mui/icons-material/Save';
import CheckIcon  from '@mui/icons-material/Check';
import { useNavigate, useLocation } from 'react-router-dom';
import authFetch from '../utils/authFetch';
import { card, btn, inputSx, tbl, NAVY, GOLD, fmtQty, PRODUCTION_BATCH_STATUS, typo } from '../styles/ds';
import SupplierAllocationSelector from '../orders/SupplierAllocationSelector';
import { rescaleComponents } from './rescaleComponents';

const CURRENCY = [{ id: 'ILS', label: '₪ שקל' }, { id: 'USD', label: '$ דולר' }];
let rk = 1;
const emptyComp = () => ({ _k: rk++, variant: null, quantity: 1, unit_cost_ils: '', unit_cost_usd: '', allocationValue: null, supplierPrices: null, supplierInventory: null });
const emptyCost = () => ({ _k: rk++, work_type: '', cost: '', currency: 'ILS', worker: null, notes: '', useGlobal: true });

const fifoFromInventory = (inv) => {
  if (!inv?.length) return { fifoIls: '', fifoUsd: '' };
  const totalQty = inv.reduce((s, r) => s + Number(r.available_qty || 0), 0);
  if (totalQty === 0) return { fifoIls: '', fifoUsd: '' };
  const wIls = inv.reduce((s, r) => s + (Number(r.unit_cost_ils || 0)) * Number(r.available_qty || 0), 0);
  const wUsd = inv.reduce((s, r) => s + (Number(r.unit_cost_usd || 0)) * Number(r.available_qty || 0), 0);
  return { fifoIls: wIls > 0 ? (wIls / totalQty).toFixed(4) : '', fifoUsd: wUsd > 0 ? (wUsd / totalQty).toFixed(4) : '' };
};
const supplierCostPatch = (allocations, inv) => {
  const allocs = (allocations || []).filter(a => Number(a.requested_quantity) > 0);
  const totalQty = allocs.reduce((s, a) => s + Number(a.requested_quantity), 0);
  if (totalQty === 0 || !inv?.length) return {};
  let wIls = 0, wUsd = 0;
  for (const a of allocs) {
    const row = inv.find(p => Number(p.supplier_id) === Number(a.supplier_id));
    const qty = Number(a.requested_quantity);
    if (row) { wIls += (Number(row.unit_cost_ils) || 0) * qty; wUsd += (Number(row.unit_cost_usd) || 0) * qty; }
  }
  const patch = {};
  if (wIls > 0) patch.unit_cost_ils = (wIls / totalQty).toFixed(4);
  if (wUsd > 0) patch.unit_cost_usd = (wUsd / totalQty).toFixed(4);
  return patch;
};

const fmt = (v, cur) => cur === 'USD'
  ? `$${Number(v||0).toLocaleString('en-US',{minimumFractionDigits:2})}`
  : `₪${Number(v||0).toLocaleString('he-IL',{minimumFractionDigits:2})}`;

export default function ProductionCreate() {
  const notify   = useNotify();
  const redirect = useRedirect();
  const navigate = useNavigate();
  const location = useLocation();

  const [finished,   setFinished]   = useState(null);
  const [quantity,   setQuantity]   = useState(1);
  const [notes,      setNotes]      = useState('');
  const [components, setComponents] = useState([emptyComp()]);
  const [costs,      setCosts]      = useState([]);
  const [saving,     setSaving]     = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [savedBatch, setSavedBatch] = useState(null);
  const [error,      setError]      = useState('');

  // Variant lists (bypass cache)
  const [finishedVars,    setFinishedVars]    = useState([]);
  const [compVars,        setCompVars]        = useState([]);
  const [allVariantsById, setAllVariantsById] = useState({}); // unfiltered — compVars excludes finished-product variants, which can themselves be BOM components (e.g. a tefillin nested in a larger set)
  const [workers,         setWorkers]         = useState([]);
  const [roles,           setRoles]           = useState([]); // worker_roles objects
  const [productRoleCosts, setProductRoleCosts] = useState({}); // { product_id: { role_name: cost_ils } }
  const [houseMatches,    setHouseMatches]    = useState({}); // { comp._k: { loading, exactMatches, closeMatches } }

  // See rescaleComponents.js — keeps every component row's per-unit ratio
  // intact when "כמות לייצור" changes after components were already picked.
  // Guarded to skip invalid/mid-typing values (empty, 0, non-numeric) so
  // clearing the field to type a new number doesn't rescale against a
  // throwaway "1".
  const prevQuantityRef = useRef(1);
  useEffect(() => {
    const newQty = Number(quantity);
    if (!Number.isFinite(newQty) || newQty <= 0) return;
    const oldQty = prevQuantityRef.current;
    if (newQty !== oldQty) {
      setComponents(cs => rescaleComponents(cs, oldQty, newQty));
    }
    prevQuantityRef.current = newQty;
  }, [quantity]);

  useEffect(() => {
    // Finished products: finished_product OR tefillin
    authFetch('/variants?_start=0&_end=500&_sort=name&_order=ASC')
      .then(r => r.json()).then(d => {
        if (!Array.isArray(d)) return;
        setFinishedVars(d.filter(v => v.product_is_finished_product)
          .map(v => ({ id: v.id, label: v.name || `#${v.id}`, quantity: v.quantity, product_id: v.product_id })));
        setCompVars(d.filter(v => !v.product_is_finished_product)
          .map(v => ({ id: v.id, label: v.name || `#${v.id}`, quantity: v.quantity, price_ils: v.price_ils, price_usd: v.price_usd, unit: v.unit || 'יחידה' })));
        setAllVariantsById(Object.fromEntries(d.map(v => [v.id, {
          id: v.id, label: v.name || `#${v.id}`, quantity: v.quantity, price_ils: v.price_ils, price_usd: v.price_usd, unit: v.unit || 'יחידה',
        }])));
      });
    authFetch('/workers?_start=0&_end=200&_sort=name&_order=ASC')
      .then(r => r.json()).then(d => {
        if (Array.isArray(d)) setWorkers(d.map(w => ({ id: w.id, label: w.name, role_ids: w.role_ids || [] })));
      });
    authFetch('/worker_roles?_start=0&_end=100&_sort=name&_order=ASC')
      .then(r => r.json()).then(d => {
        if (Array.isArray(d)) setRoles(d);
      });
    authFetch('/product_role_costs')
      .then(r => r.json()).then(d => {
        if (d?.costs) setProductRoleCosts(d.costs);
      });
  }, []);

  // BOM: choosing a finished product loads its saved recipe into the
  // components table. The very first pick (before any product was selected)
  // respects components the user may have already started typing manually —
  // but once a product IS selected, switching to a different one always
  // refreshes the components to match the new product (its BOM, or cleared
  // to empty if it has none), rather than leaving the old product's stale.
  // Afterward, auto-matching runs against the finished product itself AND
  // every loaded BOM component — some pairings (e.g. tefillin → batim) match
  // on a BOM component, others (e.g. a finished mezuzah → its klaf) match on
  // the finished product selection itself; both go through the same check.
  const pickFinished = async (v, qtyOverride) => {
    const previousFinishedId = finished?.id ?? null;
    setFinished(v);
    if (!v) return;
    const isSwitchingProduct = previousFinishedId != null && previousFinishedId !== v.id;
    const hasUserComponents  = components.some(c => c.variant);
    if (hasUserComponents && !isSwitchingProduct) return;
    const q = qtyOverride ?? quantity;

    let bomComponents = [];
    try {
      const r   = await authFetch(`/variants/${v.id}/bom`);
      const bom = await r.json();
      if (Array.isArray(bom) && bom.length > 0) {
        // Fetch supplier inventory for each BOM component to populate cost
        const invResults = await Promise.all(
          bom.map(b =>
            authFetch(`/variants/${b.component_variant_id}/supplier-inventory`)
              .then(r => r.json()).then(d => ({ variantId: b.component_variant_id, rows: d.rows || [] }))
              .catch(() => ({ variantId: b.component_variant_id, rows: [] }))
          )
        );
        const invMap = new Map(invResults.map(r => [r.variantId, r.rows]));
        bomComponents = bom.map(b => {
          const supplierInventory = invMap.get(b.component_variant_id) || [];
          const { fifoIls, fifoUsd } = fifoFromInventory(supplierInventory);
          return {
            ...emptyComp(),
            variant: compVars.find(cv => cv.id === b.component_variant_id)
                     || allVariantsById[b.component_variant_id]
                     || { id: b.component_variant_id, label: b.component_name },
            quantity: Number(b.quantity) * (Number(q) || 1),
            unit_cost_ils: fifoIls,
            unit_cost_usd: fifoUsd,
            supplierInventory,
          };
        });
      }
    } catch (_) { /* no recipe — fine, bomComponents stays empty */ }

    setComponents(bomComponents.length > 0 ? bomComponents : [{ ...emptyComp(), quantity: Number(q) || 1 }]);
    if (bomComponents.length > 0) notify('המתכון (BOM) נטען אוטומטית', { type: 'info' });

    await autoMatchComponents([{ variant: v }, ...bomComponents]);
  };

  // Pre-fill from an order line flagged short on stock (navigate state from
  // OrderEdit's "צור מנת ייצור" link) — apply once, after finishedVars loads.
  const [prefillApplied, setPrefillApplied] = useState(false);
  useEffect(() => {
    if (prefillApplied || !location.state?.finished_variant_id || finishedVars.length === 0) return;
    const id    = location.state.finished_variant_id;
    const match = finishedVars.find(v => v.id === id)
      || { id, label: location.state.finished_variant_name || `#${id}` };
    const qty = Number(location.state.quantity) || 1;
    pickFinished(match, qty);
    // pickFinished(match, qty) above already scales every component to this
    // exact qty — pre-set the rescale ref so the quantity-change effect
    // doesn't ALSO multiply them by qty a second time once setQuantity below
    // lands.
    prevQuantityRef.current = qty;
    setQuantity(qty);
    setPrefillApplied(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishedVars, prefillApplied]);

  const saveBom = async () => {
    if (!finished) { notify('נא לבחור מוצר מוגמר קודם', { type: 'warning' }); return; }
    const comps = components.filter(c => c.variant && Number(c.quantity) > 0)
      .map(c => ({ component_variant_id: c.variant.id, quantity: Number(c.quantity) / (Number(quantity) || 1) }));
    try {
      const res  = await authFetch(`/variants/${finished.id}/bom`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ components: comps }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'שגיאה בשמירת המתכון');
      notify(comps.length ? 'המתכון נשמר — ייטען אוטומטית בייצור הבא' : 'המתכון נוקה', { type: 'success' });
    } catch (e) { notify(e.message, { type: 'error' }); }
  };

  const setComp  = (k, patch) => setComponents(cs => cs.map(c => c._k === k ? { ...c, ...patch } : c));
  const setCost  = (k, patch) => setCosts(cs => cs.map(c => c._k === k ? { ...c, ...patch } : c));
  const addComp  = () => setComponents(cs => [...cs, { ...emptyComp(), quantity: Number(quantity) || 1 }]);
  const addCost  = (overrides) => setCosts(cs => [...cs, { ...emptyCost(), ...(overrides || {}) }]);
  const delComp  = k  => setComponents(cs => cs.filter(c => c._k !== k));
  const delCost  = k  => setCosts(cs => cs.filter(c => c._k !== k));

  // A component can have more than one applicable rule set at once (e.g. a
  // complete tefillin matches both a בית and a פרשה) — ruleSets is an array,
  // one entry per rule set, each carrying its own name/target and matches.
  const fetchHouseMatches = async (comp) => {
    if (!comp.variant?.id) return;
    setHouseMatches(m => ({ ...m, [comp._k]: { loading: true } }));
    try {
      const r = await authFetch(`/variants/${comp.variant.id}/matching-components`);
      const d = await r.json();
      setHouseMatches(m => ({ ...m, [comp._k]: { loading: false, ruleSets: d.ruleSets || [] } }));
    } catch {
      setHouseMatches(m => ({ ...m, [comp._k]: { loading: false, error: true } }));
    }
  };

  const addHouseComp = async (house) => {
    const houseVariant = compVars.find(v => v.id === house.id)
      || allVariantsById[house.id]
      || { id: house.id, label: house.name, unit: 'יחידה', quantity: house.quantity };
    const newComp = { ...emptyComp(), variant: houseVariant, quantity: Number(quantity) || 1 };
    setComponents(cs => [...cs, newComp]);
    try {
      const [pricesRes, invRes] = await Promise.all([
        authFetch(`/variants/${house.id}/supplier-prices`),
        authFetch(`/variants/${house.id}/supplier-inventory`),
      ]);
      const supplierPrices    = (await pricesRes.json()).rows || [];
      const supplierInventory = (await invRes.json()).rows || [];
      const { fifoIls, fifoUsd } = fifoFromInventory(supplierInventory);
      setComponents(cs => cs.map(c => c._k === newComp._k
        ? { ...c, supplierPrices, supplierInventory, unit_cost_ils: fifoIls, unit_cost_usd: fifoUsd }
        : c));
    } catch (_) {}
  };

  // Automatically add the exact-matching component for each candidate
  // variant (the finished product itself, plus every loaded BOM component)
  // that has an active matching rule set. A single candidate can have MORE
  // than one applicable rule set at once (e.g. a complete tefillin needs
  // both a matching בית and a matching פרשה) — every rule set's exact match
  // gets added, not just the first. A candidate with no applicable rule set
  // (checked server-side) or no exact match for a given rule set is silently
  // skipped — the manual "הצג התאמות" picker on a component row still works
  // for anything less than exact.
  const autoMatchComponents = async (comps) => {
    const existingIds = new Set(comps.filter(c => c.variant).map(c => c.variant.id));
    for (const c of comps) {
      if (!c.variant?.id) continue;
      try {
        const r = await authFetch(`/variants/${c.variant.id}/matching-components`);
        const d = await r.json();
        for (const ruleSet of d.ruleSets || []) {
          const best = ruleSet.exactMatches?.[0];
          if (!best || existingIds.has(best.id)) continue;
          existingIds.add(best.id);
          await addHouseComp(best);
          notify(`רכיב תואם נוסף אוטומטית (${ruleSet.ruleSetName}): ${best.name}`, { type: 'info' });
        }
      } catch (_) { /* no match — the manual picker below still works */ }
    }
  };

  // Totals
  const totalCompIls = components.reduce((s,c) => s + (Number(c.unit_cost_ils)||0)*(Number(c.quantity)||1), 0);
  const unitWorkIls  = costs.filter(c=>c.currency==='ILS').reduce((s,c)=>s+(Number(c.cost)||0),0);
  const totalWorkIls = unitWorkIls * (Number(quantity) || 1);
  const totalCostIls = totalCompIls + totalWorkIls;
  const unitCostIls  = quantity > 0 ? totalCostIls / quantity : 0;

  const handleSave = async () => {
    if (!finished) { setError('נא לבחור מוצר מוגמר'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        finished_variant_id: finished.id,
        quantity: Number(quantity) || 1,
        notes: notes || null,
        components: components.filter(c => c.variant).map(c => ({
          variant_id: c.variant.id, quantity: (Number(c.quantity)||1) / (Number(quantity)||1),
          unit_cost_ils: Number(c.unit_cost_ils)||null,
          unit_cost_usd: Number(c.unit_cost_usd)||null,
          allocation_mode: c.allocationValue?.mode || 'automatic',
          allocations: c.allocationValue?.mode === 'supplier_specific'
            ? (c.allocationValue.allocations || [])
                .filter(a => a.source_type && Number(a.requested_quantity) > 0)
                .map(a => ({ ...a, requested_quantity: Number(a.requested_quantity) / (Number(quantity)||1) }))
            : [],
        })),
        costs: costs.filter(c => c.cost && Number(c.cost)>0).map(c => ({
          worker_id: c.worker?.id||null, work_type: c.work_type||null,
          cost: Number(c.cost), currency: c.currency, notes: c.notes||null,
        })),
      };

      const method = savedBatch ? 'PUT' : 'POST';
      const url    = savedBatch
        ? `/production_batches/${savedBatch.id}`
        : '/production_batches';

      const res = await authFetch(url, {
        method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (!res.ok) { const b = await res.json().catch(()=>({})); throw new Error(b.error || 'שגיאה'); }
      const data = await res.json();
      setSavedBatch(data);
      notify('הטיוטה נשמרה', { type: 'success' });
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleConfirm = async () => {
    if (!finished) { setError('נא לבחור מוצר מוגמר'); return; }
    if (!window.confirm(`לאשר ייצור של ${quantity} יח' "${finished?.label}"?\nפעולה זו תנכה רכיבים מהמלאי ולא ניתנת לביטול.`)) return;

    setConfirming(true); setError('');
    try {
      // Step 1: save (create or update draft)
      const payload = {
        finished_variant_id: finished.id,
        quantity: Number(quantity) || 1,
        notes: notes || null,
        components: components.filter(c => c.variant).map(c => ({
          variant_id: c.variant.id, quantity: (Number(c.quantity)||1) / (Number(quantity)||1),
          unit_cost_ils: Number(c.unit_cost_ils)||null,
          unit_cost_usd: Number(c.unit_cost_usd)||null,
          allocation_mode: c.allocationValue?.mode || 'automatic',
          allocations: c.allocationValue?.mode === 'supplier_specific'
            ? (c.allocationValue.allocations || [])
                .filter(a => a.source_type && Number(a.requested_quantity) > 0)
                .map(a => ({ ...a, requested_quantity: Number(a.requested_quantity) / (Number(quantity)||1) }))
            : [],
        })),
        costs: costs.filter(c => c.cost && Number(c.cost)>0).map(c => ({
          worker_id: c.worker?.id||null, work_type: c.work_type||null,
          cost: Number(c.cost), currency: c.currency, notes: c.notes||null,
        })),
      };

      let batchId = savedBatch?.id;
      if (!batchId) {
        const saveRes = await authFetch('/production_batches', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
        if (!saveRes.ok) { const b = await saveRes.json().catch(()=>({})); throw new Error(b.error || 'שגיאה בשמירה'); }
        const saved = await saveRes.json();
        batchId = saved.id;
        setSavedBatch(saved);
      } else {
        const updateRes = await authFetch(`/production_batches/${batchId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
        if (!updateRes.ok) { const b = await updateRes.json().catch(()=>({})); throw new Error(b.error || 'שגיאה בעדכון'); }
      }

      // Step 2: confirm
      const confirmRes = await authFetch(`/production_batches/${batchId}/confirm`, { method: 'POST' });
      if (!confirmRes.ok) { const b = await confirmRes.json().catch(()=>({})); throw new Error(b.error || 'שגיאה באישור'); }
      notify('הייצור אושר! המלאי עודכן', { type: 'success' });
      redirect('/production_batches');
    } catch (e) { setError(e.message); }
    finally { setConfirming(false); }
  };

  return (
    <Box className="crm-page" p={3}>
      <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography {...typo.pageTitle}>מנת ייצור חדשה</Typography>
        {savedBatch && (
          <Chip label={`טיוטה #${savedBatch.id}`} sx={{ backgroundColor: PRODUCTION_BATCH_STATUS.draft.bg, color: PRODUCTION_BATCH_STATUS.draft.color, fontWeight: 700 }} />
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Finished product + quantity ── */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Typography fontWeight={700} color={NAVY} mb={2}>מוצר מוגמר</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}>
            <Autocomplete options={finishedVars} value={finished}
              onChange={(_, v) => pickFinished(v)}
              getOptionLabel={o => o?.label ?? ''} isOptionEqualToValue={(a,b) => a.id===b.id}
              renderInput={p => <TextField {...p} label="מוצר מוגמר *" sx={inputSx} />}
            />
            <TextField label="כמות לייצור" type="number" value={quantity}
              onChange={e => setQuantity(e.target.value.replace(/[^\d]/g, ''))}
              inputProps={{ min: 1, step: 1 }} sx={inputSx} />
          </Box>
          {finished && (
            <Typography fontSize={12} color="text.secondary" mt={1}>
              מלאי נוכחי: {fmtQty(finished.quantity ?? 0, finished.unit)}
            </Typography>
          )}
          <TextField label="הערות" value={notes} onChange={e => setNotes(e.target.value)}
            fullWidth multiline rows={3} sx={{ mt: 2, ...inputSx }}
            placeholder="הערות כלליות על מנת הייצור..." />
        </CardContent>
      </Card>

      {/* ── Components ── */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography fontWeight={700} color={NAVY}>רכיבים (פרשיות, בתים וכו')</Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" onClick={saveBom} disabled={!finished}
                title="שמירת הרכיבים הנוכחיים כמתכון קבוע למוצר זה"
                sx={btn.gold}>
                שמור כמתכון
              </Button>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={addComp}
                sx={{ backgroundColor: NAVY, color: '#fff', borderRadius: 2, '&:hover': { backgroundColor: '#162d5a' } }}>
                הוסף רכיב
              </Button>
            </Stack>
          </Stack>
          <Box data-mobile-scroll sx={{ overflowX: 'auto' }}><Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 13, py: 1.2 } }}>
                <TableCell sx={{ minWidth: 240 }}>רכיב</TableCell>
                <TableCell sx={{ width: 90 }} align="center">כמות</TableCell>
                <TableCell sx={{ width: 120 }} align="center">
                  <Tooltip title="מחיר לפי אצוות המלאי — מתמלא אוטומטית" arrow placement="top">
                    <span>עלות ₪ / יח'</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: 110 }} align="center">עלות $ / יח'</TableCell>
                <TableCell sx={{ width: 110 }} align="center">סה"כ ₪</TableCell>
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {components.map((c, i) => {
                const hasCost    = c.unit_cost_ils !== '' && Number(c.unit_cost_ils) > 0;
                const hasInvData = c.supplierInventory !== null;
                const noCostInv  = hasInvData && !hasCost;
                const costTooltip = hasCost
                  ? 'מחיר חושב אוטומטית מאצוות מלאי — ניתן לשנות ידנית'
                  : noCostInv ? 'לא נמצא מחיר ברכישות — הזן ידנית' : '';
                return (
                <Fragment key={c._k}>
                <TableRow sx={tbl.zebraRow(i)}>
                  <TableCell sx={{ py: 0.8 }}>
                    <Autocomplete size="small" options={compVars} value={c.variant}
                      onChange={async (_, v) => {
                        if (!v) { setComp(c._k, { variant: null, unit_cost_ils: '', unit_cost_usd: '', allocationValue: null, supplierPrices: null, supplierInventory: null }); return; }
                        let supplierPrices = null, supplierInventory = null;
                        try {
                          const [pricesRes, invRes] = await Promise.all([
                            authFetch(`/variants/${v.id}/supplier-prices`),
                            authFetch(`/variants/${v.id}/supplier-inventory`),
                          ]);
                          supplierPrices    = (await pricesRes.json()).rows || [];
                          supplierInventory = (await invRes.json()).rows || [];
                        } catch(_) {}
                        const { fifoIls, fifoUsd } = fifoFromInventory(supplierInventory);
                        setComp(c._k, { variant: v, unit_cost_ils: fifoIls, unit_cost_usd: fifoUsd, allocationValue: null, supplierPrices, supplierInventory });
                      }}
                      getOptionLabel={o=>o?.label??''} isOptionEqualToValue={(a,b)=>a.id===b.id}
                      renderInput={p => <TextField {...p} placeholder="חפש רכיב..." sx={inputSx} />}
                    />
                    {c.variant && (
                      <Typography fontSize={10} color="text.secondary">
                        מלאי: {fmtQty(c.variant.quantity ?? 0, c.variant.unit)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.8 }} align="center">
                    <Stack alignItems="center" spacing={0.3}>
                      <TextField size="small" type="number" value={c.quantity}
                        onChange={e => setComp(c._k, { quantity: e.target.value })}
                        inputProps={{ min: 1, step: 1, style: { textAlign: 'center' } }} sx={{ ...inputSx, width: 80 }} />
                      {c.variant?.unit && c.variant.unit !== 'יחידה' && (
                        <Typography fontSize={10} color="text.secondary">{c.variant.unit}</Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ py: 0.8 }} align="center">
                    <Tooltip title={costTooltip} arrow placement="top">
                      <TextField size="small" type="number" value={c.unit_cost_ils}
                        onChange={e => setComp(c._k, { unit_cost_ils: e.target.value })}
                        inputProps={{ min: 0, step: '0.01', style: { textAlign: 'center' } }}
                        sx={{ ...inputSx, width: 100,
                          '& .MuiInputBase-root': hasCost
                            ? { backgroundColor: 'rgba(21,87,36,0.07)' }
                            : noCostInv ? { backgroundColor: 'rgba(133,100,4,0.07)' } : {} }} />
                    </Tooltip>
                    {noCostInv && (
                      <Typography fontSize={9} color="warning.main" mt={0.3}>הזן ידנית</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.8 }} align="center">
                    <TextField size="small" type="number" value={c.unit_cost_usd}
                      onChange={e => setComp(c._k, { unit_cost_usd: e.target.value })}
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'center' } }} sx={{ ...inputSx, width: 100 }} />
                  </TableCell>
                  <TableCell sx={{ py: 0.8 }} align="center">
                    <Typography fontSize={13} fontWeight={700} color={NAVY}>
                      ₪{((Number(c.unit_cost_ils)||0)*(Number(c.quantity)||1)).toLocaleString('he-IL',{minimumFractionDigits:0})}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => delComp(c._k)} aria-label="הסרת רכיב" sx={{ color: '#c62828' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                {c.variant && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, px: 0, border: 'none', borderRight: `3px solid ${GOLD}`, backgroundColor: 'rgba(13,29,62,0.025)' }}>
                      <Box px={2} pt={0.5} pb={1.5}>
                        <SupplierAllocationSelector
                          variantId={c.variant.id}
                          quantity={Number(c.quantity) || 1}
                          unit={c.variant.unit}
                          ignoreReservations
                          value={c.allocationValue}
                          onChange={v => {
                            const patch = { allocationValue: v };
                            if (v?.mode === 'supplier_specific') {
                              Object.assign(patch, supplierCostPatch(v.allocations, c.supplierInventory));
                            } else {
                              const { fifoIls, fifoUsd } = fifoFromInventory(c.supplierInventory);
                              if (fifoIls) patch.unit_cost_ils = fifoIls;
                              if (fifoUsd) patch.unit_cost_usd = fifoUsd;
                            }
                            setComp(c._k, patch);
                          }}
                        />
                        {!!c.variant && (
                          <Box mt={1.5} pt={1.5} sx={{ borderTop: '1px dashed rgba(13,29,62,0.2)' }}>
                            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                              <Typography fontSize={12} fontWeight={700} color={NAVY}>התאמת רכיב:</Typography>
                              <Button size="small" variant="outlined"
                                disabled={houseMatches[c._k]?.loading}
                                onClick={() => fetchHouseMatches(c)}
                                sx={{ borderColor: GOLD, color: NAVY, fontSize: 11, py: 0.3, px: 1.5, borderRadius: 2, minWidth: 0 }}>
                                {houseMatches[c._k]?.loading ? <CircularProgress size={14} /> : 'הצג התאמות'}
                              </Button>
                            </Stack>
                            {houseMatches[c._k] && !houseMatches[c._k].loading && (
                              houseMatches[c._k].error ? (
                                <Typography fontSize={11} color="error.main">שגיאה בטעינת ההתאמות</Typography>
                              ) : !houseMatches[c._k].ruleSets?.length ? (
                                <Typography fontSize={11} color="text.secondary">אין כללי התאמה מוגדרים למוצר זה</Typography>
                              ) : (
                                // A component can have more than one applicable
                                // rule set at once (e.g. a complete tefillin
                                // needs both a בית and a פרשה) — one labeled
                                // group per rule set.
                                <Stack spacing={1.5}>
                                  {houseMatches[c._k].ruleSets.map(ruleSet => (
                                    <Box key={ruleSet.ruleSetId}>
                                      <Typography fontSize={11} fontWeight={700} color={NAVY} mb={0.5}>{ruleSet.ruleSetName}</Typography>
                                      {!ruleSet.exactMatches?.length && !ruleSet.closeMatches?.length && (
                                        <Typography fontSize={11} color="text.secondary">לא נמצאו התאמות</Typography>
                                      )}
                                      {ruleSet.exactMatches?.length > 0 && (
                                        <Box mb={1}>
                                          <Typography fontSize={11} fontWeight={700} color="success.dark" mb={0.5}>התאמה מדויקת:</Typography>
                                          <Stack spacing={0.5}>
                                            {ruleSet.exactMatches.map(h => (
                                              <Stack key={h.id} direction="row" alignItems="center" spacing={1}>
                                                <Chip label={h.name} size="small" sx={{ backgroundColor: 'rgba(21,87,36,0.1)', color: '#155724', fontSize: 11 }} />
                                                <Typography fontSize={11} color="text.secondary">מלאי: {h.quantity}</Typography>
                                                <Button size="small" variant="outlined"
                                                  onClick={() => addHouseComp(h)}
                                                  sx={{ borderColor: GOLD, color: NAVY, fontSize: 10, py: 0.2, px: 1, borderRadius: 2, minWidth: 0 }}>
                                                  הוסף כרכיב
                                                </Button>
                                              </Stack>
                                            ))}
                                          </Stack>
                                        </Box>
                                      )}
                                      {ruleSet.closeMatches?.length > 0 && (
                                        <Box>
                                          <Typography fontSize={11} fontWeight={700} color="warning.dark" mb={0.5}>התאמה חלקית:</Typography>
                                          <Stack spacing={0.5}>
                                            {ruleSet.closeMatches.map(h => (
                                              <Stack key={h.id} direction="row" alignItems="center" spacing={1}>
                                                <Chip label={h.name} size="small" sx={{ backgroundColor: 'rgba(133,100,4,0.1)', color: '#856404', fontSize: 11 }} />
                                                <Typography fontSize={11} color="text.secondary">מלאי: {h.quantity}</Typography>
                                                <Button size="small" variant="outlined"
                                                  onClick={() => addHouseComp(h)}
                                                  sx={{ borderColor: GOLD, color: NAVY, fontSize: 10, py: 0.2, px: 1, borderRadius: 2, minWidth: 0 }}>
                                                  הוסף כרכיב
                                                </Button>
                                              </Stack>
                                            ))}
                                          </Stack>
                                        </Box>
                                      )}
                                    </Box>
                                  ))}
                                </Stack>
                              )
                            )}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              );
            })}
            </TableBody>
          </Table></Box>
        </CardContent>
      </Card>

      {/* ── Work costs ── */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography fontWeight={700} color={NAVY}>עלויות עבודה</Typography>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={addCost}
              sx={{ backgroundColor: NAVY, color: '#fff', borderRadius: 2, '&:hover': { backgroundColor: '#162d5a' } }}>
              הוסף עלות
            </Button>
          </Stack>

          {/* Defined rates for this category */}
          {finished?.product_id && productRoleCosts[finished.product_id] &&
           Object.keys(productRoleCosts[finished.product_id]).length > 0 && (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'rgba(13,29,62,0.04)', borderRadius: 1.5,
              border: '1px solid rgba(13,29,62,0.1)' }}>
              <Typography fontSize={11} fontWeight={700} color={NAVY} mb={1}>
                תעריפים מוגדרים לקטגוריה זו:
              </Typography>
              <Stack className="crm-wrap" direction="row" flexWrap="wrap" gap={1}>
                {Object.entries(productRoleCosts[finished.product_id]).map(([role, data]) => (
                  <Chip key={role} size="small"
                    label={`${role}: ₪${Number(data.cost_ils).toLocaleString()}`}
                    sx={{ backgroundColor: '#fff', border: '1px solid rgba(13,29,62,0.2)',
                      fontSize: 11, fontWeight: 600, color: NAVY, cursor: 'pointer' }}
                    onClick={() => addCost({ work_type: role, cost: data.cost_ils, currency: data.currency || 'ILS', useGlobal: true })}
                  />
                ))}
              </Stack>
              <Typography fontSize={10} color="text.secondary" mt={0.8}>
                לחץ על תעריף להוספה מהירה לרשימת העלויות
              </Typography>
            </Box>
          )}

          {costs.length === 0
            ? <Typography fontSize={12} color="text.secondary">אין עלויות עבודה — לחץ "הוסף עלות"</Typography>
            : costs.map((c, i) => {
              const roleId      = c.work_type ? roles.find(r => r.name === c.work_type)?.id : null;
              const roleWorkers = roleId ? workers.filter(w => w.role_ids?.includes(roleId)) : workers;
              return (
              <Stack className="crm-wrap" key={c._k} direction="row" spacing={1} mb={1} alignItems="center" flexWrap="wrap">
                {/* Work type */}
                <FormControl size="small" sx={{ width: 150 }}>
                  <InputLabel sx={{ fontSize: 12 }}>סוג עבודה</InputLabel>
                  <Select value={c.work_type} label="סוג עבודה" sx={{ ...inputSx, fontSize: 12 }}
                    onChange={e => {
                      const roleName  = e.target.value;
                      const productId = finished?.product_id;
                      const roleDef   = roles.find(r => r.name === roleName);
                      const patch     = { work_type: roleName };
                      // Clear worker if it doesn't belong to the newly selected role
                      if (c.worker && roleDef) {
                        const wInState = workers.find(w => w.id === c.worker.id);
                        if (!wInState?.role_ids?.includes(roleDef.id)) patch.worker = null;
                      }
                      if (c.useGlobal) {
                        const catCost = productId && productRoleCosts[productId]?.[roleName];
                        if (catCost) {
                          patch.cost     = catCost.cost_ils;
                          patch.currency = catCost.currency || 'ILS';
                        } else if (roleDef) {
                          patch.cost     = roleDef.cost_currency === 'USD' ? roleDef.default_cost_usd : roleDef.default_cost_ils;
                          patch.currency = roleDef.cost_currency || 'ILS';
                        }
                      }
                      setCost(c._k, patch);
                    }}>
                    <MenuItem value=""><em>בחר...</em></MenuItem>
                    {roles.map(r => (
                      <MenuItem key={r.name} value={r.name} sx={{ fontSize: 12 }}>
                        <Stack className="crm-header" direction="row" justifyContent="space-between" width="100%" spacing={1}>
                          <span>{r.name}</span>
                          {(Number(r.default_cost_ils) > 0 || Number(r.default_cost_usd) > 0) && (
                            <Typography fontSize={10} color="text.secondary">
                              {r.cost_currency === 'USD' ? `$${r.default_cost_usd}` : `₪${r.default_cost_ils}`}
                            </Typography>
                          )}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Worker — filtered to role matching the selected work_type */}
                <Autocomplete size="small" options={roleWorkers} value={c.worker}
                  onChange={(_, v) => setCost(c._k, { worker: v })}
                  getOptionLabel={o=>o?.label??''} isOptionEqualToValue={(a,b)=>a.id===b.id}
                  noOptionsText={c.work_type ? 'אין עובדים לתפקיד זה' : 'בחר סוג עבודה תחילה'}
                  sx={{ width: 150 }}
                  renderInput={p => <TextField {...p} label="עובד" sx={inputSx} />} />

                {/* Cost — global toggle or manual */}
                {c.useGlobal ? (
                  <Stack alignItems="center" spacing={0.3}>
                    <Typography fontSize={13} fontWeight={700} color={NAVY}>
                      {c.currency === 'USD' ? '$' : '₪'}{Number(c.cost || 0).toLocaleString()}
                    </Typography>
                    <Button size="small" variant="text"
                      onClick={() => setCost(c._k, { useGlobal: false })}
                      sx={{ fontSize: 10, color: '#856404', p: 0, minWidth: 0, textDecoration: 'underline' }}>
                      ערוך ידנית
                    </Button>
                  </Stack>
                ) : (
                  <Stack alignItems="center" spacing={0.3}>
                    <TextField size="small" label="עלות" type="number" value={c.cost}
                      onChange={e => setCost(c._k, { cost: e.target.value })} sx={{ ...inputSx, width: 95 }} />
                    <Button size="small" variant="text"
                      onClick={() => {
                        const roleDef = roles.find(r => r.name === c.work_type);
                        setCost(c._k, {
                          useGlobal: true,
                          cost:     roleDef ? (roleDef.cost_currency === 'USD' ? roleDef.default_cost_usd : roleDef.default_cost_ils) : c.cost,
                          currency: roleDef?.cost_currency || c.currency,
                        });
                      }}
                      sx={{ fontSize: 10, color: '#155724', p: 0, minWidth: 0, textDecoration: 'underline' }}>
                      השתמש בגלובלי
                    </Button>
                  </Stack>
                )}

                {/* Currency (only in manual mode) */}
                {!c.useGlobal && (
                  <FormControl size="small" sx={{ width: 80 }}>
                    <Select value={c.currency} onChange={e => setCost(c._k, { currency: e.target.value })} sx={inputSx}>
                      {CURRENCY.map(cu => <MenuItem key={cu.id} value={cu.id}>{cu.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}

                <IconButton size="small" onClick={() => delCost(c._k)} aria-label="הסרת עלות" sx={{ color: '#c62828' }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
              );
            })
          }
        </CardContent>
      </Card>

      {/* ── Cost summary ── */}
      <Card sx={{ ...card.base, mb: 3, backgroundColor: '#f8f5f0', border: `1px solid ${GOLD}` }}>
        <CardContent className="crm-card-content" sx={{ p: 2.5 }}>
          <Typography fontWeight={700} color={NAVY} mb={1.5}>סיכום עלויות (₪)</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4,1fr)' }, gap: 2 }}>
            <Box><Typography fontSize={12} color="text.secondary">עלות קנייה - רכיבים</Typography><Typography fontWeight={700}>₪{totalCompIls.toLocaleString('he-IL',{minimumFractionDigits:0})}</Typography></Box>
            <Box><Typography fontSize={12} color="text.secondary">עלות קנייה - עבודה (₪{unitWorkIls.toLocaleString('he-IL',{minimumFractionDigits:0})}/יח' × {quantity})</Typography><Typography fontWeight={700}>₪{totalWorkIls.toLocaleString('he-IL',{minimumFractionDigits:0})}</Typography></Box>
            <Box><Typography fontSize={12} color="text.secondary">סה"כ עלות קנייה</Typography><Typography fontWeight={700} fontSize={16} color={NAVY}>₪{totalCostIls.toLocaleString('he-IL',{minimumFractionDigits:0})}</Typography></Box>
            <Box><Typography fontSize={12} color="text.secondary">עלות קנייה ליחידה</Typography><Typography fontWeight={800} fontSize={16} color={NAVY}>₪{unitCostIls.toLocaleString('he-IL',{minimumFractionDigits:2})}</Typography></Box>
          </Box>
        </CardContent>
      </Card>

      {/* ── Actions ── */}
      <Stack className="crm-actions" direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="outlined" onClick={() => navigate(-1)} sx={btn.cancel}>ביטול</Button>
        <Button variant="outlined" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave} disabled={saving || confirming}
          sx={{ borderColor: NAVY, color: NAVY, borderRadius: 2 }}>
          שמור טיוטה
        </Button>
        <Button variant="contained"
          startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
          onClick={handleConfirm} disabled={saving || confirming || !finished}
          sx={{ ...btn.primaryLg, backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' } }}>
          {savedBatch ? 'אשר ייצור' : 'שמור ואשר'}
        </Button>
      </Stack>
    </Box>
  );
}
